import { API_ENDPOINTS, apiRequest, buildQuery } from '../config/api';

// Types — single source of truth lives in ../types, mirroring the backend schemas.
import {
  Operator,
  GtPrefix,
  ApiLog,
  UnknownNetwork,
  StatsOverview,
  CountryStats,
  TelegramConfig,
  CountryOption,
  LogQuery,
  TimelineStats,
  Page,
  Brand,
  ImsiSeries,
  BrandNetwork,
  BrandNetworkUpdate,
  BrandResolveResult,
  AppUser,
  CurrentUser,
} from '../types';

export type {
  Operator, GtPrefix, ApiLog, UnknownNetwork, StatsOverview, CountryStats, TelegramConfig, Page,
  Brand, ImsiSeries, BrandNetwork, BrandNetworkUpdate, BrandResolveResult,
  AppUser, CurrentUser,
};

// API Service interface
interface ApiService {
  getOperators: (params?: {
    page?: number;
    // fastapi-pagination's parameter is `size`; `limit` was silently dropped,
    // so the page was always the server default of 50.
    size?: number;
    country?: string;
    agreement_type?: string;
    is_allowed?: boolean;
    search?: string;
  }) => Promise<Page<Operator>>;
  getOperator: (id: number) => Promise<Operator>;
  createOperator: (data: any) => Promise<Operator>;
  updateOperator: (id: number, data: any) => Promise<Operator>;
  deleteOperator: (id: number) => Promise<void>;
  bulkToggleOperators: (ids: number[], is_allowed: boolean) => Promise<{ updated: number }>;
  getGtPrefixes: (operatorId: number) => Promise<GtPrefix[]>;
  addGtPrefix: (operatorId: number, data: any) => Promise<GtPrefix>;
  updateGtPrefix: (id: number, data: any) => Promise<GtPrefix>;
  deleteGtPrefix: (id: number) => Promise<void>;
  getLogs: (params?: LogQuery) => Promise<Page<ApiLog>>;
  exportLogs: (params?: LogQuery) => void;
  getUnknownNetworks: (params?: any) => Promise<any>;
  resolveUnknownNetwork: (id: number) => Promise<void>;
  createOperatorFromUnknown: (id: number, data: any) => Promise<Operator>;
  getStatsOverview: () => Promise<any>;
  getStatsByCountry: (params?: { days?: number }) => Promise<CountryStats[]>;
  getStatsByOperator: (params?: any) => Promise<any>;
  getStatsTimeline: (params?: { hours?: number; interval?: 'hour' | 'day' }) => Promise<TimelineStats[]>;
  getTopUnknown: (params?: any) => Promise<any>;
  getOperatorCountries: () => Promise<CountryOption[]>;
  getTelegramConfig: () => Promise<TelegramConfig>;
  updateTelegramConfig: (data: {
    bot_token?: string;
    chat_id?: string;
    notifications_enabled?: boolean;
    notification_threshold?: number;
    traffic_alert_enabled?: boolean;
    traffic_alert_window_minutes?: number;
    stuck_alert_enabled?: boolean;
    stuck_alert_min_denials?: number;
  }) => Promise<TelegramConfig>;
  testTelegramConfig: () => Promise<{ success: boolean; detail: string }>;
  testHlrSteering: (data: { imsi: string; gt_vlr: string }) => Promise<any>;
  testHssSteering: (data: { imsi: string; mcc_mnc: string }) => Promise<any>;

  getBrands: (params?: { name?: string; is_active?: boolean }) => Promise<Brand[]>;
  getBrand: (id: number) => Promise<Brand>;
  createBrand: (data: Partial<Brand> & { copy_global_allow_list?: boolean }) => Promise<Brand>;
  updateBrand: (id: number, data: Partial<Brand>) => Promise<Brand>;
  deleteBrand: (id: number) => Promise<void>;
  resolveImsi: (imsi: string) => Promise<BrandResolveResult>;

  getImsiSeries: (brandId: number) => Promise<ImsiSeries[]>;
  addImsiSeries: (brandId: number, data: { imsi_from: string; imsi_to: string; label?: string }) => Promise<ImsiSeries>;
  updateImsiSeries: (brandId: number, seriesId: number, data: { imsi_from?: string; imsi_to?: string; label?: string }) => Promise<ImsiSeries>;
  deleteImsiSeries: (brandId: number, seriesId: number) => Promise<void>;

  getBrandNetworks: (brandId: number) => Promise<BrandNetwork[]>;
  updateBrandNetworks: (brandId: number, entries: BrandNetworkUpdate[]) => Promise<{ updated: number }>;
  copyGlobalTemplateToBrand: (brandId: number) => Promise<{ updated: number }>;

  login: (username: string, password: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<CurrentUser>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ message: string }>;

  getUsers: () => Promise<AppUser[]>;
  createUser: (data: {
    username: string; password: string; full_name?: string | null;
    role: string; brand_id?: number | null; is_active?: boolean;
  }) => Promise<AppUser>;
  updateUser: (id: number, data: {
    full_name?: string | null; role?: string; brand_id?: number | null;
    is_active?: boolean; password?: string;
  }) => Promise<AppUser>;
  deleteUser: (id: number) => Promise<void>;
}

// API Service
export const apiService: ApiService = {
  // Operators
  async getOperators(params?: {
    page?: number;
    size?: number;
    country?: string;
    agreement_type?: string;
    is_allowed?: boolean;
    search?: string;
  }) {
    // Trailing slash: the route is declared as "/", so the slash-free URL
    // costs a 307 redirect on every list load.
    return apiRequest<Page<Operator>>(`${API_ENDPOINTS.OPERATORS}/${buildQuery(params)}`);
  },

  async getOperator(id: number) {
    return apiRequest<Operator>(API_ENDPOINTS.OPERATOR_BY_ID(id));
  },

  async createOperator(data: Partial<Operator>) {
    return apiRequest<Operator>(API_ENDPOINTS.OPERATORS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateOperator(id: number, data: Partial<Operator>) {
    return apiRequest<Operator>(API_ENDPOINTS.OPERATOR_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteOperator(id: number) {
    return apiRequest<void>(API_ENDPOINTS.OPERATOR_BY_ID(id), {
      method: 'DELETE',
    });
  },

  // GT Prefixes
  async getGtPrefixes(operatorId: number) {
    return apiRequest<GtPrefix[]>(API_ENDPOINTS.OPERATOR_GT_PREFIXES(operatorId));
  },

  async addGtPrefix(operatorId: number, data: { cc_ndc: string; gt_node?: string; mgt?: string }) {
    return apiRequest<GtPrefix>(API_ENDPOINTS.OPERATOR_GT_PREFIXES(operatorId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateGtPrefix(prefixId: number, data: { cc_ndc?: string; gt_node?: string; mgt?: string }) {
    return apiRequest<GtPrefix>(API_ENDPOINTS.GT_PREFIX_BY_ID(prefixId), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteGtPrefix(prefixId: number) {
    return apiRequest<void>(API_ENDPOINTS.GT_PREFIX_BY_ID(prefixId), {
      method: 'DELETE',
    });
  },

  async bulkToggleOperators(operatorIds: number[], isAllowed: boolean) {
    return apiRequest<{ updated: number }>(API_ENDPOINTS.OPERATORS_BULK_TOGGLE, {
      method: 'POST',
      body: JSON.stringify({ operator_ids: operatorIds, is_allowed: isAllowed }),
    });
  },


  // Logs
  // Names match the endpoint: it reads `size`, `date_from` and `date_to`.
  // The previous signature declared page/limit/start_date/end_date, of which
  // only `page` was ever real -- so every other filter was silently dropped.
  async getLogs(params?: LogQuery) {
    return apiRequest<Page<ApiLog>>(
      `${API_ENDPOINTS.LOGS}/${buildQuery({ ...params })}`
    );
  },

  async exportLogs(params?: LogQuery) {
    const response = await fetch(`${API_ENDPOINTS.LOGS_EXPORT}${buildQuery({ ...params })}`, {
      credentials: 'include',
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_export_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  // Unknown Networks
  async getUnknownNetworks(params?: {
    page?: number;
    limit?: number;
    resolved?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiRequest<{ items: UnknownNetwork[]; total: number }>(
      `${API_ENDPOINTS.UNKNOWN_NETWORKS}?${queryParams}`
    );
  },

  async resolveUnknownNetwork(id: number) {
    return apiRequest<void>(API_ENDPOINTS.UNKNOWN_NETWORK_RESOLVE(id), {
      method: 'PUT',
    });
  },

  async createOperatorFromUnknown(
    id: number,
    data: {
      tadig_code: string;
      country: string;
      operator_name: string;
      mcc_mnc?: string;
      cc_ndc?: string;
    }
  ) {
    return apiRequest<Operator>(API_ENDPOINTS.UNKNOWN_NETWORK_ADD_OPERATOR(id), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Stats
  async getStatsOverview() {
    return apiRequest<StatsOverview>(API_ENDPOINTS.STATS_OVERVIEW);
  },

  // `days` is a rolling look-back window, which is what the endpoint accepts.
  // This used to declare start_date/end_date -- names the backend has never
  // read -- and only type-checked because the interface below typed it `any`.
  async getStatsByCountry(params?: { days?: number }) {
    return apiRequest<CountryStats[]>(
      `${API_ENDPOINTS.STATS_BY_COUNTRY}${buildQuery(params)}`
    );
  },

  async getStatsByOperator(params?: {
    start_date?: string;
    end_date?: string;
    country?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiRequest<any[]>(`${API_ENDPOINTS.STATS_BY_OPERATOR}?${queryParams}`);
  },

  // Same stale-name problem the by-country method had: the endpoint reads
  // `hours` and `interval`, never start_date/end_date.
  async getStatsTimeline(params?: {
    hours?: number;
    interval?: 'hour' | 'day';
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return apiRequest<any[]>(`${API_ENDPOINTS.STATS_TIMELINE}?${queryParams}`);
  },

  async getTopUnknown(limit: number = 10) {
    return apiRequest<UnknownNetwork[]>(
      `${API_ENDPOINTS.STATS_TOP_UNKNOWN}?limit=${limit}`
    );
  },

  async getOperatorCountries() {
    return apiRequest<CountryOption[]>(API_ENDPOINTS.OPERATOR_COUNTRIES);
  },

  // Config
  async getTelegramConfig() {
    return apiRequest<TelegramConfig>(API_ENDPOINTS.CONFIG_TELEGRAM);
  },

  async updateTelegramConfig(data: {
    bot_token?: string;
    chat_id?: string;
    notifications_enabled?: boolean;
    notification_threshold?: number;
    traffic_alert_enabled?: boolean;
    traffic_alert_window_minutes?: number;
    stuck_alert_enabled?: boolean;
    stuck_alert_min_denials?: number;
  }) {
    // The API answers with the saved state, so the caller can render it back
    // without a second round trip.
    return apiRequest<TelegramConfig>(API_ENDPOINTS.CONFIG_TELEGRAM, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async testTelegramConfig() {
    return apiRequest<{ success: boolean; detail: string }>(
      API_ENDPOINTS.CONFIG_TELEGRAM_TEST,
      { method: 'POST' }
    );
  },

  // Test steering endpoints
  async testHlrSteering(data: { imsi: string; gt_vlr: string }) {
    return apiRequest<any>(API_ENDPOINTS.STEERING_HLR, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async testHssSteering(data: { imsi: string; mcc_mnc: string }) {
    return apiRequest<any>(API_ENDPOINTS.STEERING_HSS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Brands
  async getBrands(params?: { name?: string; is_active?: boolean }) {
    return apiRequest<Brand[]>(`${API_ENDPOINTS.BRANDS}${buildQuery(params)}`);
  },

  async getBrand(id: number) {
    return apiRequest<Brand>(API_ENDPOINTS.BRAND_BY_ID(id));
  },

  async createBrand(data: Partial<Brand> & { copy_global_allow_list?: boolean }) {
    return apiRequest<Brand>(API_ENDPOINTS.BRANDS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateBrand(id: number, data: Partial<Brand>) {
    return apiRequest<Brand>(API_ENDPOINTS.BRAND_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteBrand(id: number) {
    return apiRequest<void>(API_ENDPOINTS.BRAND_BY_ID(id), { method: 'DELETE' });
  },

  async resolveImsi(imsi: string) {
    return apiRequest<BrandResolveResult>(
      `${API_ENDPOINTS.BRAND_RESOLVE}${buildQuery({ imsi })}`
    );
  },

  // IMSI series
  async getImsiSeries(brandId: number) {
    return apiRequest<ImsiSeries[]>(API_ENDPOINTS.BRAND_IMSI_SERIES(brandId));
  },

  async addImsiSeries(brandId: number, data: { imsi_from: string; imsi_to: string; label?: string }) {
    return apiRequest<ImsiSeries>(API_ENDPOINTS.BRAND_IMSI_SERIES(brandId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateImsiSeries(
    brandId: number,
    seriesId: number,
    data: { imsi_from?: string; imsi_to?: string; label?: string }
  ) {
    return apiRequest<ImsiSeries>(API_ENDPOINTS.BRAND_IMSI_SERIES_BY_ID(brandId, seriesId), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteImsiSeries(brandId: number, seriesId: number) {
    return apiRequest<void>(API_ENDPOINTS.BRAND_IMSI_SERIES_BY_ID(brandId, seriesId), {
      method: 'DELETE',
    });
  },

  // Per-brand allow list
  async getBrandNetworks(brandId: number) {
    return apiRequest<BrandNetwork[]>(API_ENDPOINTS.BRAND_NETWORKS(brandId));
  },

  // One transactional write. There is deliberately no per-row endpoint: two
  // write paths for the same data are two chances to disagree, and being
  // all-or-nothing is what lets the UI say truthfully that nothing was saved.
  async updateBrandNetworks(brandId: number, entries: BrandNetworkUpdate[]) {
    return apiRequest<{ updated: number }>(API_ENDPOINTS.BRAND_NETWORKS(brandId), {
      method: 'PUT',
      body: JSON.stringify({ entries }),
    });
  },

  async copyGlobalTemplateToBrand(brandId: number) {
    return apiRequest<{ updated: number }>(API_ENDPOINTS.BRAND_NETWORKS_COPY_TEMPLATE(brandId), {
      method: 'POST',
      body: JSON.stringify({ overwrite: true }),
    });
  },

  // Auth. Nothing here handles a token: the session is an httpOnly cookie the
  // browser attaches on its own, which is exactly why JavaScript cannot read
  // or steal it.
  async login(username: string, password: string) {
    return apiRequest<CurrentUser>(API_ENDPOINTS.AUTH_LOGIN, {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },

  async logout() {
    return apiRequest<void>(API_ENDPOINTS.AUTH_LOGOUT, { method: 'POST' });
  },

  async getCurrentUser() {
    return apiRequest<CurrentUser>(API_ENDPOINTS.AUTH_ME);
  },

  async changePassword(currentPassword: string, newPassword: string) {
    return apiRequest<{ message: string }>(API_ENDPOINTS.AUTH_CHANGE_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  // Users
  async getUsers() {
    return apiRequest<AppUser[]>(API_ENDPOINTS.USERS);
  },

  async createUser(data: any) {
    return apiRequest<AppUser>(API_ENDPOINTS.USERS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateUser(id: number, data: any) {
    return apiRequest<AppUser>(API_ENDPOINTS.USER_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteUser(id: number) {
    return apiRequest<void>(API_ENDPOINTS.USER_BY_ID(id), { method: 'DELETE' });
  },
};